
import React from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, BarChart3, RefreshCw, Search } from 'lucide-react';
import { PremiumBotSignalsTable } from './PremiumBotSignalsTable';
import { PremiumCTraderSignalsTable } from './PremiumCTraderSignalsTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePremiumBotSignals } from '@/hooks/use-premium-bot-signals';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TablePagination } from '@/components/ui/table-pagination';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface PremiumBotSignalsSectionProps {
  botId: string | undefined;
  isIntegrated: boolean;
}

export const PremiumBotSignalsSection = ({ botId, isIntegrated }: PremiumBotSignalsSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // If the user hasn't integrated with this bot, don't show the signals
  if (!isIntegrated || !botId) {
    return null;
  }

  const { 
    botSignals,
    ctraderSignals,
    botSignalsTotal,
    ctraderSignalsTotal,
    botSignalsPage,
    botSignalsPageSize,
    ctraderSignalsPage,
    ctraderSignalsPageSize,
    isBotSignalsLoading,
    isCtraderSignalsLoading,
    botSignalsError,
    ctraderSignalsError,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    handleBotSignalsPageChange,
    handleBotSignalsPageSizeChange,
    handleCtraderSignalsPageChange,
    handleCtraderSignalsPageSizeChange,
    refreshSignals
  } = usePremiumBotSignals(botId);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  if (isBotSignalsLoading && isCtraderSignalsLoading) {
    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <div className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} bg-primary/20 rounded`} />
            </div>
            <div>
              <div className={`h-${isMobile ? "6" : "8"} w-48 bg-muted rounded`} />
              <div className={`h-4 w-64 bg-muted/60 rounded mt-1`} />
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-lg bg-muted`} />
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`}>
      {/* Enhanced Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-blue-500/5 rounded-2xl blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <BarChart3 className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
            </div>
            <div>
              <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-foreground`}>
                {t("signal_history")}
              </h2>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
                Track and analyze trading signals and execution status
              </p>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Enhanced Card */}
      <Card className="overflow-hidden border hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-background/50">
        <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
          {/* Enhanced Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className={`absolute left-2.5 top-2.5 ${isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} text-muted-foreground`} />
                <Input
                  type="search"
                  placeholder="Search signals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${isMobile ? "pl-7 h-9 text-sm w-full" : "pl-8 w-[250px]"} bg-background/50 border-input/50 focus:border-primary/50`}
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`${isMobile ? "w-[100px] h-9 text-sm" : "w-[110px]"} bg-background/50 border-input/50`}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "sm"}
                onClick={refreshSignals}
                className={`${isMobile ? "h-9 px-3" : ""} border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200`}
              >
                <RefreshCw className={`${isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
                {t("refresh")}
              </Button>
            </div>
          </div>

          {/* Enhanced Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className={`grid w-full grid-cols-2 sm:w-auto ${isMobile ? "h-9" : ""}`}>
              <TabsTrigger value="bot-signals" className={isMobile ? "text-xs h-7 px-3" : ""}>Bot Signals</TabsTrigger>
              <TabsTrigger value="ctrader-signals" className={isMobile ? "text-xs h-7 px-3" : ""}>CTrader Signals</TabsTrigger>
            </TabsList>
            
            <TabsContent value="bot-signals" className="mt-6">
              <div className="flex flex-col space-y-4">
                {botSignalsError ? (
                  <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm border border-red-200/50">
                    <div className="flex items-center mb-1">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <p className="font-medium">Error loading signals</p>
                    </div>
                    <p className="ml-6">Please try refreshing the page.</p>
                  </div>
                ) : (
                  <>
                    <ScrollArea className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-md border border-border/50 bg-background/30`}>
                      <PremiumBotSignalsTable 
                        signals={botSignals} 
                        isLoading={isBotSignalsLoading}
                        currentPage={botSignalsPage}
                        pageSize={botSignalsPageSize}
                        totalItems={botSignalsTotal}
                        onPageChange={handleBotSignalsPageChange}
                        onPageSizeChange={handleBotSignalsPageSizeChange}
                      />
                    </ScrollArea>
                    
                    <TablePagination
                      currentPage={botSignalsPage}
                      pageSize={botSignalsPageSize}
                      totalItems={botSignalsTotal}
                      onPageChange={handleBotSignalsPageChange}
                      onPageSizeChange={handleBotSignalsPageSizeChange}
                    />
                  </>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="ctrader-signals" className="mt-6">
              <div className="flex flex-col space-y-4">
                {ctraderSignalsError ? (
                  <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm border border-red-200/50">
                    <div className="flex items-center mb-1">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <p className="font-medium">Error loading CTrader signals</p>
                    </div>
                    <p className="ml-6">Please try refreshing the page.</p>
                  </div>
                ) : (
                  <>
                    <ScrollArea className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-md border border-border/50 bg-background/30`}>
                      <PremiumCTraderSignalsTable 
                        signals={ctraderSignals}
                        isLoading={isCtraderSignalsLoading}
                        currentPage={ctraderSignalsPage}
                        pageSize={ctraderSignalsPageSize}
                        totalItems={ctraderSignalsTotal}
                        onPageChange={handleCtraderSignalsPageChange}
                        onPageSizeChange={handleCtraderSignalsPageSizeChange}
                      />
                    </ScrollArea>
                    
                    <TablePagination
                      currentPage={ctraderSignalsPage}
                      pageSize={ctraderSignalsPageSize}
                      totalItems={ctraderSignalsTotal}
                      onPageChange={handleCtraderSignalsPageChange}
                      onPageSizeChange={handleCtraderSignalsPageSizeChange}
                    />
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Card>
    </div>
  );
};
